import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { Heart, Bed, Bath, Home } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLocalizedNavigation } from '../../hooks/useLocalizedNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { PropertyMarkerProps } from '../../types';

// Format price for marker display (short format)
const formatPriceShort = (price: number): string => {
  if (price >= 1000000) {
    return `$${(price / 1000000).toFixed(1)}M`;
  } else if (price >= 1000) {
    return `$${Math.round(price / 1000)}K`;
  }
  return `$${price}`;
};

// Format price for popup (full format)
const formatPriceFull = (price: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Create custom price marker icon
const createPriceIcon = (price: number, isSelected: boolean, isHovered: boolean) => {
  const priceText = formatPriceShort(price);
  const baseClasses = 'price-marker';
  const stateClasses = isSelected ? 'selected' : isHovered ? 'hovered' : '';

  return L.divIcon({
    className: `${baseClasses} ${stateClasses}`,
    html: `<div class="price-marker-content">${priceText}</div>`,
    iconSize: [70, 28],
    iconAnchor: [35, 28],
    popupAnchor: [0, -28],
  });
};

const PropertyMarker: React.FC<PropertyMarkerProps> = ({
  property,
  isSelected,
  isHovered,
  onClick,
  onHover,
}) => {
  const { t } = useTranslation();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();
  const { user, toggleSaveProperty, isPropertySaved } = useAuth();
  const isSaved = isPropertySaved(property.id);

  const icon = createPriceIcon(property.price, isSelected, isHovered);

  const handleSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (user) {
      toggleSaveProperty(property.id);
    }
  };

  return (
    <Marker
      position={[property.latitude, property.longitude]}
      icon={icon}
      eventHandlers={{
        click: () => onClick(property.id),
        mouseover: () => onHover(property.id),
        mouseout: () => onHover(null),
      }}
    >
      <Popup className="property-popup">
        <div className="w-56">
          {/* Image */}
          <div className="relative">
            <img
              src={property.mainImage}
              alt={getTextInCurrentLanguage(property.title)}
              className="w-full h-36 object-cover rounded-t-lg"
            />
            {user && (
              <Button
                onClick={handleSaveClick}
                variant="ghost"
                size="icon"
                className={`absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white ${
                  isSaved ? 'text-red-500' : 'text-gray-600'
                }`}
              >
                <Heart className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
              </Button>
            )}
          </div>

          {/* Content */}
          <div className="p-3">
            <div className="font-bold text-xl text-blue-600 mb-1">
              {formatPriceFull(property.price)}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-sm text-gray-700 mb-2">
              <span className="flex items-center gap-1">
                <Bed className="h-4 w-4" />
                {property.beds}
              </span>
              <span className="flex items-center gap-1">
                <Bath className="h-4 w-4" />
                {property.baths}
              </span>
              <span className="flex items-center gap-1">
                <Home className="h-4 w-4" />
                {property.sqft.toLocaleString()}
              </span>
            </div>

            {/* Address */}
            <p className="text-xs text-gray-600 mb-3">
              {property.address}, {property.city}
            </p>

            {/* View Details Button */}
            <Link
              to={getLocalizedPath(`/property/${property.id}`)}
              className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {t('mapSearch.viewDetails')}
            </Link>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

export default PropertyMarker;
